import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, AlertTriangle, ExternalLink } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Stat, Empty, ago, money, RateBadge, CONGESTION_STYLE } from "./common";

export default function FreightDashboard() {
  const { user } = useAuth();
  const canSync = hasPerm(user, "sea_freight", "create");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/sea-freight/dashboard").then((r) => setD(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sea-freight/sync");
      toast.success(`Synced: ${data.news.added} new article(s), ${data.alerts.fired} alert(s) fired`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!d) return <div className="py-20 text-center text-slate-400" data-testid="freight-dashboard-loading">Loading freight intelligence...</div>;

  return (
    <div data-testid="freight-dashboard">
      <PageHead title="Global Freight Dashboard"
        subtitle="Worldwide sea freight market, port status, disruptions and your monitored routes — with the source and timestamp on every figure."
        right={canSync && <Button data-testid="sync-now-btn" variant="outline" className="rounded-sm gap-1.5" disabled={busy} onClick={sync}>
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Sync now
        </Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat testid="stat-rates" label="Rate observations" value={d.market.rate_observations}
          hint={`${d.market.bookable_rates} bookable · ${d.market.indicative_rates} indicative`} />
        <Stat testid="stat-routes" label="Monitored routes" value={d.market.tracked_routes} hint="Saved routes" />
        <Stat testid="stat-ports" label="Ports in database" value={d.ports.total}
          hint={`${d.ports.with_status} with recorded status`} />
        <Stat testid="stat-alerts" label="Active alert rules" value={d.active_alert_rules}
          hint={`${d.quotations_expiring} quotation(s) expiring in 7 days`} tone={d.quotations_expiring ? "red" : "slate"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white border border-slate-200 rounded-sm">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 font-head">Top shipping disruptions</h2>
              <Link to="/sea-freight/news" className="text-xs text-slate-500 hover:text-slate-900">All news</Link>
            </header>
            <div className="divide-y divide-slate-100">
              {(d.disruptions || []).length === 0 && <div className="px-4 py-8 text-sm text-slate-400">No disruption headlines collected yet. Run a sync.</div>}
              {(d.disruptions || []).map((n) => (
                <a key={n.id} href={n.url} target="_blank" rel="noreferrer" data-testid={`disruption-${n.id}`}
                  className="block px-4 py-3 hover:bg-slate-50">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm text-slate-800 font-medium">{n.headline}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{n.source_name} · {ago(n.published_at)} · {(n.categories || []).join(", ")}</div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 font-head">Saved route monitoring</h2>
              <Link to="/sea-freight/saved-routes" className="text-xs text-slate-500 hover:text-slate-900">Manage</Link>
            </header>
            <div className="divide-y divide-slate-100">
              {(d.routes || []).length === 0 && <div className="px-4 py-8 text-sm text-slate-400">No saved routes yet — add Shanghai → Mundra and other lanes you watch.</div>}
              {(d.routes || []).map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`route-card-${r.id}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-800">{r.route_name}</div>
                    <div className="text-[11px] text-slate-500">{r.container_type} · {r.rate_points} observation(s)</div>
                  </div>
                  <div className="text-right">
                    {r.latest_rate ? (
                      <>
                        <div className="text-sm font-mono tabular-nums text-slate-900">{money(r.latest_rate.total_freight, r.latest_rate.currency)}</div>
                        <div className="text-[11px] text-slate-500">
                          {r.change_pct !== null && r.change_pct !== undefined && (
                            <span className={r.change_pct > 0 ? "text-red-600" : r.change_pct < 0 ? "text-emerald-600" : ""}>{r.change_pct > 0 ? "+" : ""}{r.change_pct}% · </span>
                          )}
                          {ago(r.latest_rate.observed_at)}
                        </div>
                      </>
                    ) : <span className="text-xs text-amber-700">Live rate unavailable</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 font-head">Latest shipping news</h2>
              <Link to="/sea-freight/news" className="text-xs text-slate-500 hover:text-slate-900">Open</Link>
            </header>
            <div className="divide-y divide-slate-100">
              {(d.news || []).slice(0, 6).map((n) => (
                <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="block px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700">
                  {n.headline}
                  <span className="text-[11px] text-slate-400 block">{n.source_name} · {ago(n.published_at)}</span>
                </a>
              ))}
              {(d.news || []).length === 0 && <div className="px-4 py-8 text-sm text-slate-400">No articles collected yet.</div>}
            </div>
          </section>
        </div>

        <div className="space-y-4">
          <section className="bg-white border border-slate-200 rounded-sm p-4" data-testid="port-status-widget">
            <h2 className="text-base font-semibold text-slate-900 font-head mb-3">Worldwide port status</h2>
            <div className="space-y-1.5">
              {Object.entries(d.ports.congestion).map(([lvl, count]) => (
                <div key={lvl} className="flex items-center justify-between text-sm">
                  <Badge variant="outline" className={`rounded-full px-2 text-[11px] ${CONGESTION_STYLE[lvl]}`}>{lvl}</Badge>
                  <span className="font-mono tabular-nums text-slate-700">{count}</span>
                </div>
              ))}
            </div>
            <Link to="/sea-freight/ports" className="text-xs text-slate-500 hover:text-slate-900 mt-3 inline-block">Port intelligence →</Link>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-4" data-testid="data-health-widget">
            <h2 className="text-base font-semibold text-slate-900 font-head mb-2">Data source health</h2>
            <div className="text-sm text-slate-700 space-y-1">
              <div className="flex justify-between"><span>Active sources</span><span className="font-mono">{d.data_sources.active}/{d.data_sources.total}</span></div>
              <div className="flex justify-between"><span>Awaiting API key</span><span className="font-mono">{d.data_sources.awaiting_key}</span></div>
              <div className="flex justify-between"><span>Failing</span><span className={`font-mono ${d.data_sources.failed ? "text-red-600" : ""}`}>{d.data_sources.failed}</span></div>
              <div className="flex justify-between"><span>Last successful sync</span><span className="text-xs text-slate-500">{ago(d.last_successful_sync)}</span></div>
            </div>
            {d.currency?.rates && (
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                USD → {Object.entries(d.currency.rates).map(([k, v]) => `${k} ${v}`).join(" · ")}
                <span className="block text-[11px] text-slate-400">{d.currency.source} · {d.currency.as_of}</span>
              </div>
            )}
            {!d.ais.ais_connected && (
              <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">{d.ais.message}</p>
            )}
            <Link to="/sea-freight/data-sources" className="text-xs text-slate-500 hover:text-slate-900 mt-3 inline-block">Manage sources →</Link>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-4" data-testid="recent-quotes-widget">
            <h2 className="text-base font-semibold text-slate-900 font-head mb-2">Recent freight quotations</h2>
            {(d.quotations || []).length === 0 ? <Empty testid="quotes-empty">No quotations recorded yet.</Empty> : (
              <div className="space-y-2">
                {d.quotations.map((q) => (
                  <div key={q.id} className="text-sm flex justify-between gap-2">
                    <span className="text-slate-700 truncate">{q.provider_name} · {q.origin_port} → {q.destination_port}</span>
                    <span className="font-mono tabular-nums text-slate-900 shrink-0">{money(q.total_freight, q.currency)}</span>
                  </div>
                ))}
              </div>
            )}
            <Link to="/sea-freight/quotations" className="text-xs text-slate-500 hover:text-slate-900 mt-3 inline-block">All quotations →</Link>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-4" data-testid="active-alerts-widget">
            <h2 className="text-base font-semibold text-slate-900 font-head mb-2">Recent alerts</h2>
            {(d.alerts || []).length === 0 ? <Empty testid="alerts-empty">No alerts fired yet.</Empty> : (
              <div className="space-y-2">
                {d.alerts.map((a) => (
                  <div key={a.id} className="text-sm">
                    <div className="text-slate-800">{a.title}</div>
                    <div className="text-[11px] text-slate-400">{ago(a.at)} · {(a.channels || []).join(", ")}</div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/sea-freight/alerts" className="text-xs text-slate-500 hover:text-slate-900 mt-3 inline-block">Smart alerts →</Link>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-4">
            <h2 className="text-base font-semibold text-slate-900 font-head mb-2">Latest rate observations</h2>
            {(d.market.latest_rates || []).length === 0 ? <Empty testid="rates-empty">Live rate unavailable. Request a freight quotation.</Empty> : (
              <div className="space-y-2.5">
                {d.market.latest_rates.map((r) => (
                  <div key={r.id} className="text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-700 truncate">{r.origin_port} → {r.destination_port}</span>
                      <span className="font-mono tabular-nums shrink-0">{money(r.total_freight, r.currency)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <RateBadge row={r} />
                      <span className="text-[11px] text-slate-400">{ago(r.observed_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/sea-freight/rates" className="text-xs text-slate-500 hover:text-slate-900 mt-3 inline-flex items-center gap-1">Freight rate search <ExternalLink className="w-3 h-3" /></Link>
          </section>
        </div>
      </div>
    </div>
  );
}
